import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import jwt_decode from 'jwt-decode';
import { LinkedAccounts, LoggedInUserDto } from 'src/app/core/interfaces/loggedInUserDto';
import { HttpService } from 'src/app/core/services/http/http.service';
import { Constants } from 'src/app/shared/constants';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { NotificationService } from 'src/app/shared/services/notification-service';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { BroadcastService } from 'src/app/shared/services/broadcast.service';
import { ChangePassword } from '../../classes/change-password-request';
import { ForgotPassword } from '../../classes/forgot-password-request';
import { SendOtpRequest } from '../../classes/sendOtpRequest';
import { LoginRequest } from '../../interfaces/login-request';
import { IPolicyAcceptanceDetail } from '../../interfaces/policy-acceptance-detail';
import { EndPoints } from 'src/app/shared/EndPoints';
import { environment } from 'src/environments/environment';
import { MfaOtpRequest } from '../../classes/mfa-otp-request';
import { VerifyMfaCode } from '../../classes/verify-mfa-code-request';
import { LoginResponse } from '../../interfaces/login-response';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  loginApi: string;
  dialogClosedFlag = false;
  passwdExpryNotificationDate: Date;
  currentdate = new Date(Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate()
));
  passwordresetOn: Date;
  passwordExpirydate: Date;
  expiryDays = 360;
  notificationDays = 15;
  notificatonDate: Date;
  dialogRef: any;
  linkedAccounts: LinkedAccounts[]
  constructor(
    private httpService: HttpService,
    private idle: Idle,
    private router: Router,
    private notification: NotificationService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private dialogService: DialogService,
    private broadCastService: BroadcastService
  ) {}

  public Login(loginRequest: LoginRequest) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.login,
      loginRequest
    );
  }

  public SetPassword(setPasswordRequest: ChangePassword) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.setPassword,
      setPasswordRequest
    );
  }

  public ForgotPassword(forgotPasswordRequest: ForgotPassword) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.FORGOT_PASSWORD,
      forgotPasswordRequest
    );
  }

  public VerifyMfaCode(verifyMfaCode: VerifyMfaCode) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.VERIFY_MFA_CODE,
      verifyMfaCode
    );
  }

  public SendOtp(emailRequest: SendOtpRequest) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.SEND_OTP,
      emailRequest
    );
  }
  
  public SendLoginMfaOtp(mfaOtpRequest: MfaOtpRequest) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.SendLoginMfaOTP,
      mfaOtpRequest
    );
  }

  public ChangePassword(changePasswordRequest: ChangePassword) {
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.CHANGE_PASSWORD,
      changePasswordRequest
    );
  }

  public refreshToken() {
    const request = {
      clientId: null,
      policyName: null,
      userObjectId: this.GetClaims().oid,
      refreshToken: this.GetAuthInfo().refreshToken,
      sessionKey: this.GetAuthInfo().sessionKey
    };
    return this.httpService.Post(
      environment.IdentityServiceUrl +
      EndPoints.REFRESH_TOKEN, request);
  }

  public GetUserPermissions(): string[] {
    return (this.localStorageService.GetClaimsTransformed(this.GetClaims().extension_permissions as string));
  }

  public Logout() {
    this.dialogService.closeAllDialog();
    this.idle.stop();
    this.notification.dismissSnackbar();
    if (this.GetLoggedInUserDetails()) {
      const azureUserId = this.GetLoggedInUserDetails().azureId;
      const sessionKey = this.GetAuthInfo()?.sessionKey;
      if (sessionKey){
        this.httpService.Post(environment.IdentityServiceUrl +
          `${EndPoints.logout}/${azureUserId}/${sessionKey}`, null).subscribe();
      }
    }
    this.localStorageService.clear();
    this.sessionStorageService.removeItem('tobeGenerated');
    this.sessionStorageService.removeItem('isAnnoucementShown');
    this.sessionStorageService.removeItem('ProjectSelected');
    this.sessionStorageService.removeItem('OrderAccountCode');
    this.sessionStorageService.removeItem('OrderAccountName');
    this.sessionStorageService.removeItem('OrderAccountState');
    this.sessionStorageService.removeItem('OrderAccountCountry');
    this.sessionStorageService.removeItem('OrderBuCode');
    this.broadCastService.setNavLinksRef(null);
    this.router.navigate(['account'], { replaceUrl: true });
  }

  public FetchLoggedInUserDetails(userObjectId: string) {
    return this.httpService.Get<LoggedInUserDto>(
      environment.customerPortalServiceUrl +
      EndPoints.GET_USER_DETAILS +
      '/' + userObjectId
    );
  }

  public SetClaims() {
    this.localStorageService.setItem(
      Constants.CLAIMS,
      JSON.stringify(this.DecodeToken(this.GetAuthInfo().idToken))
    );
  }

  public GetClaims() {
  return JSON.parse(this.localStorageService.getItem(Constants.CLAIMS));
  }

  public SetAuthInfo(loginResponse: LoginResponse) {
    this.localStorageService.setItem(
      Constants.AUTH_INFO,
      JSON.stringify(loginResponse)
    );
  }

  public DecodeToken(token: string): any {
    return jwt_decode(token);
  }

  public GetAuthInfo(): LoginResponse {
    return JSON.parse(this.localStorageService.getItem(Constants.AUTH_INFO));
  }

  public GetBasicAuthToken() {
    return this.broadCastService.getIdbasicToken();
  }

  public GetAzureBasicAuthToken() : any {
    return environment.azureBasicAuthToken;
  }
  public SetLoggedInUserDetails(data) {
    this.localStorageService.setItem(Constants.LOGGED_IN_USER_INFO, JSON.stringify(data));
  }

  public SetUserPolicyAcceptanceDetails(details) {
    this.localStorageService.setItem('userUnreadPolicyDetails', JSON.stringify(details));
  }

  public GetUserPolicyAcceptanceDetails(): IPolicyAcceptanceDetail {
    return JSON.parse(this.localStorageService.getItem('userUnreadPolicyDetails'));
  }

  public GetLoggedInUserDetails(): LoggedInUserDto {
    return JSON.parse(this.localStorageService.getItem(Constants.LOGGED_IN_USER_INFO));
  }

  public ClearData() {
      this.dialogService.closeAllDialog();
      this.idle.stop();
      this.notification.dismissSnackbar();
      this.localStorageService.clear();
      this.sessionStorageService.removeItem('tobeGenerated');
      this.sessionStorageService.removeItem('isAnnoucementShown');
      this.sessionStorageService.removeItem('ProjectSelected');
      this.broadCastService.setNavLinksRef(null);
      this.router.navigate(['account'], { replaceUrl: true });
    }

  public GetAuthorisedControlList() {
    return JSON.parse(localStorage.getItem(Constants.AUTHORIZED_CONTROLS));
  }

  public IsAuthenticated(): boolean {
    const info = JSON.parse(this.localStorageService.getItem(Constants.CLAIMS));
    if ((info !== '')
      && (info !== null)
      && (info !== 'undefined')
      && (new Date(new Date(0).setUTCSeconds(this.GetClaims().exp).valueOf() - new Date().getUTCSeconds()) > new Date())) {
      return true;
    } else {
      return false;
    }
  }

  public IsMfaVerified(): boolean {
    const info = this.localStorageService.getItem(Constants.IS_VERIFICATION_SUCCESSFUL);
    if ((info !== '')
      && (info !== null)
      && (info !== 'undefined')
      && (info !== 'false')
      ) {
      return true;
    } else {
      return false;
    }
  }

  public tokenTimeout() {
    if (this.GetClaims()) {
      let secondsRemaining = 0;

      let delta = Math.abs(new Date(0).setUTCSeconds(
        this.GetClaims().exp).valueOf() - new Date().valueOf()) / 1000;

      const days = Math.floor(delta / 86400);
      delta -= days * 86400;
      secondsRemaining += days * 86400;

      const hours = Math.floor(delta / 3600) % 24;
      delta -= hours * 3600;
      secondsRemaining += hours * 3600;

      const minutes = Math.floor(delta / 60) % 60;
      delta -= minutes * 60;
      secondsRemaining += minutes * 60;

      const seconds = Math.floor(delta % 60);
      secondsRemaining += seconds;
      return secondsRemaining;
    } else {
      return undefined;
    }
  }

  public IsSetPassword(): boolean {
    const info = JSON.parse(this.localStorageService.getItem(Constants.AUTH_INFO));
    if (info !== '' && info !== null && info !== 'undefined') {
      return true;
    } else {
      return false;
    }
  }

  public HasLabConfiguration(): boolean {
    const info = JSON.parse(this.localStorageService.getItem(Constants.LOGGED_IN_USER_INFO));
    const claims = JSON.parse(this.localStorageService.getItem(Constants.CLAIMS));
    const extensionPermissions = this.localStorageService.GetClaimsTransformed(claims.extension_permissions)
    .map((item: string) => item.trim());
    if (info.type.toString().toLowerCase() === Constants.INTERNAL_USER &&
      extensionPermissions.findIndex((x) => x.toString().toLowerCase() === 'CanManageLabConfiguration'.toLowerCase()) !== -1) {
      return true;
    } else {
      return false;
    }
  }

  addDaysToDate(date: Date, days: number) {
    const date1 = new Date(date);
    date1.setDate(date1.getDate() + days);
    return date1;
  }

  checkPasswordExpiryStatus() {
    const self = this;
    const claims = self.GetClaims();
    if (typeof (claims.extension_passwordResetOn) === 'string') {
      self.passwordresetOn = claims.extension_passwordResetOn;
    } else {
      self.passwordresetOn = new Date(new Date(0).setUTCSeconds(claims.extension_passwordResetOn)
        .valueOf() - new Date().getUTCSeconds());
    }
    self.passwordExpirydate  = self.addDaysToDate(self.passwordresetOn,
      parseInt(environment.PwdLifeTime, 10));
    self.notificatonDate = self.addDaysToDate(self.passwordExpirydate ,
      parseInt(environment.FirstReminder, 10));
    self.passwdExpryNotificationDate = self.notificatonDate;
    const hasDialogFlag = self.localStorageService.getItem(Constants.DIALOG_FLAG);
    if ((!hasDialogFlag || hasDialogFlag == null || hasDialogFlag === 'false')
      && this.stripTime(self.currentdate) >= this.stripTime(self.notificatonDate)
      && this.stripTime(self.currentdate) < this.stripTime(self.passwordExpirydate )) {
      self.dialogService.openPasswordExpiryDialog(this.getDaysDifference(self.passwordExpirydate ,self.currentdate));
    }
  }

  stripTime(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
}

  getDaysDifference(dateInUtc: Date, normalDate: Date): number {
    const utcDate = Date.UTC(normalDate.getFullYear(), normalDate.getMonth(), normalDate.getDate());
    return Math.floor((dateInUtc.getTime() - utcDate) / (1000 * 60 * 60 * 24));
  }

  SetNGetBasicTokenFromSession(callback) {
    this.broadCastService.setIdbasicToken(environment.idmBasicAuthToken);
  }
}
